import React, { useEffect, useState } from 'react';
import { X, ExternalLink } from 'lucide-react';
import VenueCheckBadge from './VenueCheckBadge';
import { formatDateTime } from '../../utils/dateFormat';
import { fetchJSON } from '../../utils/api';
import { detailLabel, detailValue } from '../workflow/constants';

// The GPS evidence behind ONE check-in — opened by clicking the Venue Check
// badge ("when someone disputes it I need something to go on"). Everything
// here is what was true at the moment of the scan; the only
// screen in the system that shows raw coordinates.
//
// Shared, not copied: the management Training Records tab
// and the dashboard's Participation Records tab open the same panel, so a
// disputed check-in reads identically wherever it is opened from. It still
// fetches one row at a time from an endpoint behind `ld:training:edit` — the
// caller decides who gets a clickable badge, and a caller that cannot pass
// that check must not offer the click at all.
//
// Coordinates print to 6 decimals — the precision the column stores, ~0.1 m,
// well past what any phone actually knows. The accuracy figure next to them is
// the honest bound, and it is stated precisely because it is NOT part of the
// verdict: a 12 m reading with ±40 m accuracy is in range on the arithmetic
// and worth arguing about in person, which is the whole point of this panel.
const AttendanceLocationModal = ({ recordId, onClose }) => {
  const [location, setLocation] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetchJSON(`/api/ld/records/attendance/${recordId}/location`)
      .then((data) => { if (!cancelled) setLocation(data.location); })
      .catch((e) => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, [recordId]);

  const metres = (m) => (m == null ? '—' : (m >= 10 ? `${Math.round(m)} m` : `${Math.round(m * 10) / 10} m`));
  const coords = (lat, lng) => (lat == null ? '—' : `${Number(lat).toFixed(6)}, ${Number(lng).toFixed(6)}`);
  const mapsUrl = (lat, lng) =>
    `https://www.google.com/maps/search/?api=1&query=${Number(lat).toFixed(6)},${Number(lng).toFixed(6)}`;

  const Row = ({ label, children }) => (
    <div className="flex items-baseline justify-between gap-4 py-2 border-b border-slate-100 last:border-0">
      <span className={`${detailLabel} shrink-0`}>{label}</span>
      <span className={`${detailValue} mt-0 text-right min-w-0`}>{children}</span>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-slate-900">Check-In Location</h3>
            <p className="text-sm text-slate-500 truncate">
              {location ? `${location.staffName} · ${location.courseTitle}` : `Attendance #${recordId}`}
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
          )}
          {!error && location === null && <p className="text-sm text-slate-500">Loading…</p>}

          {location && location.latitude == null && (
            <p className="text-sm text-slate-500">
              This check-in has no location reading — it was not a QR scan, or the phone&apos;s
              location was off or denied at the time. Nothing was recorded and nothing can be
              reconstructed after the fact.
            </p>
          )}

          {location && location.latitude != null && (
            <>
              <div className="flex items-center gap-2">
                <VenueCheckBadge
                  value={location.inRange}
                  distanceM={location.distanceM}
                  radiusM={location.judgedRadiusM}
                />
                <span className="text-xs text-slate-500">
                  scanned {formatDateTime(location.recordedAt)}
                </span>
              </div>

              <div className="border border-slate-200 rounded-lg px-4 py-1">
                <Row label="Recorded Position">
                  <span className="font-mono text-xs">{coords(location.latitude, location.longitude)}</span>
                  <a
                    href={mapsUrl(location.latitude, location.longitude)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ml-2 inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                  >
                    <ExternalLink className="w-3 h-3" /> Map
                  </a>
                </Row>
                <Row label="Reading Accuracy">
                  {location.accuracyM == null
                    ? <span className="text-slate-600">not reported</span>
                    : `± ${metres(location.accuracyM)}`}
                </Row>
                <Row label="Venue">
                  {location.venue ? location.venue.name : <span className="text-slate-600">none set</span>}
                </Row>
                {location.venue && location.venue.latitude != null && (
                  <Row label="Venue Position">
                    <span className="font-mono text-xs">
                      {coords(location.venue.latitude, location.venue.longitude)}
                    </span>
                    <a
                      href={mapsUrl(location.venue.latitude, location.venue.longitude)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ml-2 inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                    >
                      <ExternalLink className="w-3 h-3" /> Map
                    </a>
                  </Row>
                )}
                <Row label="Distance">
                  {location.distanceM == null
                    ? <span className="text-slate-600">cannot be measured — the venue has no coordinates</span>
                    : (
                      <>
                        {metres(location.distanceM)}
                        {location.judgedRadiusM != null && (
                          <span className="text-slate-600"> of {metres(location.judgedRadiusM)} allowed</span>
                        )}
                      </>
                    )}
                </Row>
              </div>

              {/* The venue has been resized since this row was judged.
                  Say it outright: otherwise the panel shows a verdict and a
                  radius that do not add up, and the reader concludes the
                  system is wrong rather than that the venue moved. */}
              {location.judgedRadiusM != null && location.venue?.radiusM != null
                && Number(location.judgedRadiusM) !== Number(location.venue.radiusM) && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  This check-in was judged against a {metres(location.judgedRadiusM)} radius.
                  The venue now allows {metres(location.venue.radiusM)} — the verdict above was
                  not re-made, and the reading it rests on has not changed.
                </p>
              )}
            </>
          )}
        </div>

        <div className="flex justify-end p-6 border-t border-slate-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default AttendanceLocationModal;
